/* ============================================================
   Gulf country jobseeker guides + quarterly market update.
   Copy is the reviewed draft in frontend/docs/CONTENT_GULF_GUIDES_DRAFT.md.
   FEE RULE: money is only ever "No registration fee" — never "free".
   IP RULE: no screening/vetting/trade-test/etc. — outcome + ease only.

   ⚠ GATE: GUIDES_PUBLISHED is false until the client verifies the ⚠ figures in the
   draft doc (live CBK rates, Saudi SAR 1,000 decree scope, Kenya BLA status, and the
   Kuwait domestic-worker restriction). While false: pages render for review but are
   noindex, unlinked, and NOT in the sitemap/SSG route list. To publish, see
   "How to publish the guides" in CONTENT_GULF_GUIDES_DRAFT.md.
   ============================================================ */

export const GUIDES_PUBLISHED = false;

/* Month/year shown as the guides' freshness stamp — bump on each review. */
export const GUIDES_UPDATED = "July 2026";

/* Quarterly Gulf market update (jobseeker-facing bullets). */
export const GULF_UPDATE = {
  lede: "The Gulf is changing in favour of the worker — here's where things stand in 2026.",
  intro:
    "Across the Gulf, the old sponsorship rules are being loosened and pay is now tracked electronically, so wages are harder to withhold. Here is what's new, in plain words.",
  points: [
    "Saudi Arabia set its first-ever minimum wage for foreign workers — SAR 1,000 a month (about KSh 34,500) from February 2026. Salaries there must now be paid digitally through the official Musaned system, so there's a record of every payment.",
    "Kenya and Saudi Arabia signed a new labour agreement in July 2026 to strengthen ethical recruitment and worker protection — one of several agreements Kenya now holds with Gulf states.",
    "Qatar's minimum wage stands at QAR 1,000 basic, plus housing and food allowances if the employer doesn't provide them — and it applies to every worker, every sector.",
    "Every Gulf country you can work in now runs a Wage Protection System — your salary is paid through a bank or approved channel and reported to the labour ministry, not handed over in cash.",
    "You should only ever travel through an NEA-licensed agency. In 2025 Kenya removed hundreds of rogue agencies from the register. Confirm any agency at diaspora.go.ke/verify and the NEA register before you pay anyone a shilling. Adel Group is licensed — NEA RA/2025/09/56.",
  ],
};

/* One shared shape per country: lede + intro + labelled fact blocks + a plain-words
   rights list, plus an optional caveat note. */
export const GULF_GUIDES = [
  {
    slug: "saudi-arabia",
    country: "Saudi Arabia",
    lede: "The largest Gulf market for Kenyan workers, and the one changing fastest.",
    intro:
      "More Kenyans work in Saudi Arabia than anywhere else in the Gulf, and in 2026 it introduced its first minimum wage for foreign workers and made digital salary payment the rule.",
    facts: [
      { label: "Pay", body: "A new minimum of SAR 1,000 a month (about KSh 34,500) for foreign workers from February 2026. Many skilled and semi-skilled roles pay more — we confirm the exact salary with the employer before you agree to anything." },
      { label: "Your visa and travel are the employer's cost", body: "Under Saudi rules the employer pays your recruitment, work permit, medical and residence permit — and since October 2025 it is illegal for an employer to charge a domestic worker a recruitment fee. You should never pay for the job itself. With Adel there is no registration fee to begin." },
      { label: "Your salary is paid digitally", body: "Wages are paid through the official Musaned system, so there is a record of every payment, every month." },
    ],
    rights: [
      "A written contract in Saudi Riyals",
      "Your passport stays yours — keeping it from you is illegal",
      "A paid weekly rest day and daily rest for domestic workers",
      "End-of-service pay when your contract ends",
    ],
  },
  {
    slug: "united-arab-emirates",
    country: "United Arab Emirates",
    lede: "Dubai and Abu Dhabi — and some of the Gulf's strongest worker protections.",
    intro:
      "The UAE overhauled its labour law in 2022 and tightened salary-payment rules again in 2026.",
    facts: [
      { label: "Pay", body: "There is no single legal minimum for foreign workers, so pay is set by the role — we confirm the salary, and whether housing, transport and meals are included, before you agree. Many blue-collar roles come with employer-provided accommodation and transport." },
      { label: "Your permit and visa are the employer's cost", body: "The work permit, entry visa, medical, Emirates ID, residence visa and health insurance are all paid by the employer under UAE law. You should never be charged for them. With Adel there is no registration fee." },
      { label: "Your salary is protected", body: "From June 2026, wages must be paid by the 1st of each month through the Wage Protection System." },
    ],
    rights: [
      "A registered MOHRE contract — verbal deals are invalid",
      "The freedom to change employer when your contract ends",
      "End-of-service gratuity after one year",
      "Unemployment insurance if you lose your job",
    ],
  },
  {
    slug: "qatar",
    country: "Qatar",
    lede: "A clear minimum wage that covers everyone, and post-World-Cup labour reforms.",
    intro:
      "Qatar was the first Gulf state to set a non-discriminatory minimum wage and to drop the “NOC” that used to tie a worker to one employer.",
    facts: [
      { label: "Pay", body: "The minimum is QAR 1,000 a month basic (about KSh 35,500), and if the employer doesn't provide housing and food they must add QAR 500 and QAR 300 — up to about QAR 1,800 (roughly KSh 64,000). It applies to every worker and every sector. Skilled roles pay more." },
      { label: "Your visa is the employer's cost", body: "The work visa, medical and Qatar ID are all paid by the employer. You should never pay for your own job. With Adel there is no registration fee." },
      { label: "You can change employer", body: "After serving notice, without your employer's permission, and you no longer need an exit permit to travel home." },
      { label: "Your salary is paid through a bank", body: "Monthly, under Qatar's Wage Protection System." },
    ],
    rights: [
      "A registered contract",
      "The freedom to change jobs after notice",
      "No exit permit needed to travel home",
      "End-of-service pay",
    ],
  },
  {
    slug: "oman",
    country: "Oman",
    lede: "Steady demand and, from 2025, strong new protections for domestic and care workers.",
    intro:
      "Oman rewrote its labour law in 2023 and, in 2025, brought in a dedicated domestic-worker law.",
    facts: [
      { label: "Pay", body: "Oman sets a legal minimum only for its own nationals, so foreign-worker pay is set by the role — we confirm the salary and what's included (housing and food are usually provided) before you agree." },
      { label: "Your visa is the employer's cost", body: "The work permit, medical and residence ID are paid by the employer. You should never pay for the job. With Adel there is no registration fee." },
      { label: "Your salary is paid through a bank", body: "Under Oman's Wage Protection System." },
    ],
    rights: [
      "A written contract",
      "Up to 12 hours' work with at least 8 hours' rest, plus weekly and annual leave",
      "Health insurance and accommodation from the employer",
      "Passport retention and forced labour are banned; end-of-service pay",
    ],
  },
  {
    slug: "bahrain",
    country: "Bahrain",
    lede: "The Gulf's earliest labour reformer, with electronic wage protection tightening in 2026.",
    intro:
      "Bahrain was the first Gulf state to ease sponsorship, and in 2026 it strengthened how wages are paid and monitored.",
    facts: [
      { label: "Pay", body: "Bahrain has no across-the-board legal minimum for the private sector, so pay is set by the role — we confirm the salary and what's included before you agree." },
      { label: "Your permit and visa are the employer's cost", body: "The work permit, medical, ID and monthly levy are the employer's responsibility, and charging a worker for recruitment or a permit is prohibited. You should never pay for the job. With Adel there is no registration fee." },
      { label: "Your salary is protected", body: "From 2026, employers must file monthly salaries through Bahrain's enhanced Wage Protection System." },
    ],
    rights: [
      "A written contract",
      "The ability to change employer through the LMRA",
      "Protection against paying for your own recruitment or permit",
    ],
    note: "The old “flexi permit” (working without an employer) has ended — a Kenyan worker needs an employer-sponsored job. Any offer of a “free visa” to work on your own is a warning sign.",
  },
  {
    slug: "kuwait",
    country: "Kuwait",
    lede: "A set minimum wage that covers foreign workers, for private-sector roles.",
    intro:
      "Kuwait sets a private-sector minimum wage that applies to foreign workers too, with wages paid and monitored through the banking system.",
    facts: [
      { label: "Pay", body: "Kuwait's private-sector minimum is KWD 75 a month (about KSh 31,000), and unlike some neighbours it applies to foreign workers too. Skilled roles pay more — we confirm the salary before you agree." },
      { label: "Your visa is the employer's cost", body: "The work permit, entry visa, residence, medical and civil ID are the employer's responsibility, and charging you for them is illegal. You should never pay for the job. With Adel there is no registration fee." },
      { label: "Your salary is paid through a bank", body: "Under Kuwait's Wage Protection System." },
    ],
    rights: [
      "A written contract",
      "Passport confiscation is a criminal offence",
      "The labour authority can move you to a new employer if an employer breaks the law, such as going three months without paying you",
    ],
  },
];

export function getGulfGuide(slug) {
  return GULF_GUIDES.find((g) => g.slug === slug);
}
